import { useEffect, useState } from "react";
import { Wallet, Save, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const WALLET_FIELDS: { key: string; label: string; placeholder: string; hint: string }[] = [
  { key: "donation_wallet_btc", label: "Bitcoin (BTC)", placeholder: "bc1q… or 1… / 3…", hint: "Bitcoin mainnet address" },
  { key: "donation_wallet_eth", label: "Ethereum (ETH)", placeholder: "0x…", hint: "ERC-20 compatible address" },
  { key: "donation_wallet_usdt", label: "Tether (USDT)", placeholder: "T… (TRC-20) or 0x… (ERC-20)", hint: "Specify chain in your description if needed" },
];

export function DonationWalletAdmin() {
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from("site_settings")
        .select("key, value")
        .in("key", WALLET_FIELDS.map((f) => f.key));
      const map: Record<string, string> = {};
      WALLET_FIELDS.forEach((f) => (map[f.key] = ""));
      (data ?? []).forEach((r: any) => (map[r.key] = r.value ?? ""));
      setValues(map);
      setLoading(false);
    }
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const updatedBy = userData.user?.id ?? null;
      const rows = WALLET_FIELDS.map((f) => ({
        key: f.key,
        value: (values[f.key] ?? "").trim(),
        updated_by: updatedBy,
        updated_at: new Date().toISOString(),
      }));
      const { error } = await supabase.from("site_settings").upsert(rows, { onConflict: "key" });
      if (error) throw error;
      toast({ title: "Saved", description: "Donation wallet addresses updated" });
    } catch (err: any) {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="glass-card p-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="glass-card p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Wallet className="w-5 h-5 text-primary" />
        <h3 className="text-lg font-semibold text-foreground">Donation Wallet Addresses</h3>
      </div>
      <p className="text-xs text-muted-foreground">
        Public addresses shown on the Pricing page so supporters can send crypto donations. Leave a field blank to hide that wallet.
      </p>
      <div className="space-y-3">
        {WALLET_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-xs font-medium text-foreground">{f.label}</label>
            <input
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={values[f.key] ?? ""}
              onChange={(e) => setValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
              placeholder={f.placeholder}
              className="w-full mt-1 bg-background border border-border/50 rounded-lg px-3 py-2 text-sm font-mono text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
            <p className="text-[10px] text-muted-foreground/60 mt-1">{f.hint}</p>
          </div>
        ))}
      </div>
      <button
        onClick={save}
        disabled={saving}
        className="w-full py-2.5 rounded-lg bg-gradient-to-r from-primary to-secondary text-primary-foreground font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {saving ? "Saving…" : "Save wallet addresses"}
      </button>
    </div>
  );
}
